import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { resolveUploadUrl } from '../../utils/apiClient';
import { useCurrentUser, useRefreshUser } from '../../context/UserContext';
import { usePermissions } from '../../hooks/usePermission';
import styles from './DashboardUI.module.css';
import settingsStyles from './Settings.module.css';
import * as profileService from '../../services/profileService';
import { uploadCompanyLogo, updateCompanyCurrency } from '../../services/companyService';
import { CURRENCIES, DEFAULT_CURRENCY } from '../../utils/currency';
import Toast from '../../components/Toast';
import TeamDefaultsManager from '../../components/settings/TeamDefaultsManager';
import LabelManager from '../../components/settings/LabelManager';
import LeaveTypeManager from '../../components/settings/LeaveTypeManager';
import ProjectStatusManager from '../../components/settings/ProjectStatusManager';
import LeadActionManager from '../../components/settings/LeadActionManager';
import CustomFieldManager from '../../components/settings/CustomFieldManager';
import CustomDropdown from '../../components/forms/CustomDropdown';
import useMinimumLoader from '../../hooks/useMinimumLoader';
import {
  ProfileIcon,
  SecurityIcon,
  PreferencesIcon,
  CompanyIcon,
  GeneralIcon,
  LabelsIcon,
  RolesIcon,
  LeaveIcon,
  StatusIcon,
  ActionsIcon,
  CustomFieldsIcon,
  CameraIcon,
  BillingIcon,
} from './SettingsIcons';
import BillingManager from '../../components/settings/BillingManager';

const NOTIFICATION_GROUPS = [
  {
    label: 'Time Off',
    events: [
      { event_type: 'timeoff_request_created', label: 'Time off request submitted' },
      { event_type: 'timeoff_request_approved', label: 'Time off request approved' },
      { event_type: 'timeoff_request_rejected', label: 'Time off request rejected' },
      { event_type: 'timeoff_request_updated', label: 'Time off request updated' },
      { event_type: 'timeoff_request_cancelled', label: 'Time off request cancelled' },
    ],
  },
  {
    label: 'Schedule',
    events: [
      { event_type: 'schedule_booking_created', label: 'Booking added' },
      { event_type: 'schedule_booking_updated', label: 'Booking updated' },
      { event_type: 'schedule_booking_deleted', label: 'Booking removed' },
    ],
  },
  {
    label: 'Timesheets',
    events: [
      { event_type: 'timesheet_created', label: 'Timesheet entry created' },
      { event_type: 'timesheet_updated', label: 'Timesheet entry updated' },
      { event_type: 'timesheet_deleted', label: 'Timesheet entry deleted' },
    ],
  },
  {
    label: 'Projects',
    events: [
      { event_type: 'project_created', label: 'Project created' },
      { event_type: 'project_updated', label: 'Project updated' },
      { event_type: 'milestone_created', label: 'Milestone created' },
      { event_type: 'milestone_updated', label: 'Milestone updated' },
      { event_type: 'project_assignment', label: 'Assigned to project' },
      { event_type: 'milestone_assignment', label: 'Assigned to milestone' },
    ],
  },
  {
    label: 'Leads',
    events: [
      { event_type: 'lead_accepted', label: 'Lead converted to project' },
      { event_type: 'lead_assignment', label: 'Assigned to lead' },
    ],
  },
  {
    label: 'System',
    events: [
      { event_type: 'invite_accepted', label: 'Invite accepted' },
    ],
  },
];

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useCurrentUser();
  const refreshUser = useRefreshUser();
  const { canManageCompanySettings, canManageBilling } = usePermissions();

  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);
  const [toast, setToast] = useState(null);

  const activeSection = searchParams.get('section') || 'profile';
  const activeCompanyTab = searchParams.get('tab') || 'general';

  // Profile state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [jobTitle, setJobTitle] = useState('');
  const [initialProfile, setInitialProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  });

  // Security state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({});
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [theme, setTheme] = useState('light');
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');
  const [timezone, setTimezone] = useState('Africa/Johannesburg');

  // Company state
  const [companyName, setCompanyName] = useState('');
  const [companyLogo, setCompanyLogo] = useState(null);
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [savingCurrency, setSavingCurrency] = useState(false);

  useEffect(() => {
    if (currentUser) {
      const nameParts = (currentUser.name || '').trim().split(' ');
      const first = nameParts[0] || '';
      const last = nameParts.slice(1).join(' ') || '';
      setFirstName(first);
      setLastName(last);
      setEmail(currentUser.email || '');
      setPhoneNumber(currentUser.phone_number || '');
      setAvatar(resolveUploadUrl(currentUser.avatar_url) || null);
      setJobTitle(currentUser.job_title || '');
      setInitialProfile({
        firstName: first,
        lastName: last,
        email: currentUser.email || '',
        phoneNumber: currentUser.phone_number || '',
      });

      setCompanyName(currentUser.company_name || '');
      setCompanyLogo(currentUser.company_logo_url || '/favicon.png');
      setCurrency(currentUser.company_currency || DEFAULT_CURRENCY);

      setLoading(false);
    }
  }, [currentUser]);

  const setActiveSection = (section) => {
    const params = { section };
    if (section === 'company') params.tab = activeCompanyTab;
    setSearchParams(params);
  };

  const setActiveCompanyTab = (tab) => {
    setSearchParams({ section: 'company', tab });
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const profileDirty =
    firstName !== initialProfile.firstName ||
    lastName !== initialProfile.lastName ||
    email !== initialProfile.email ||
    phoneNumber !== initialProfile.phoneNumber;

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setToast(null);

    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      await profileService.updatePersonalInfo(currentUser.user_id, {
        name: fullName,
        email,
        phone_number: phoneNumber,
      });
      await refreshUser();
      showToast('Profile updated successfully', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const result = await profileService.uploadAvatar(currentUser.user_id, file);
      setAvatar(resolveUploadUrl(result.avatar_url));
      await refreshUser();
      showToast('Avatar uploaded successfully', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setToast(null);

    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }

    try {
      await profileService.updatePassword({ currentPassword, newPassword });
      showToast('Password updated successfully', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  useEffect(() => {
    if (activeSection === 'preferences' && !prefsLoaded) {
      profileService.getNotificationPreferences().then((data) => {
        const map = {};
        for (const p of data.preferences) {
          map[p.event_type] = { in_app: p.in_app, email: p.email };
        }
        setNotificationPrefs(map);
        setPrefsLoaded(true);
      }).catch(() => {});
    }
  }, [activeSection, prefsLoaded]);

  const handlePrefToggle = async (eventType, channel, value) => {
    const current = notificationPrefs[eventType] || { in_app: true, email: false };
    const updated = { ...notificationPrefs, [eventType]: { ...current, [channel]: value } };
    setNotificationPrefs(updated);
    try {
      const preferences = Object.entries(updated).map(([event_type, pref]) => ({
        event_type,
        in_app: pref.in_app,
        email: pref.email,
      }));
      await profileService.updateNotificationPreferences(preferences);
      showToast('Preferences updated', 'success');
    } catch (err) {
      showToast(err.message, 'error');
      setNotificationPrefs((prev) => ({ ...prev, [eventType]: current }));
    }
  };

  const handleCompanyLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setToast(null);
    try {
      await uploadCompanyLogo(file);
      if (typeof refreshUser === 'function') {
        await refreshUser();
      }
      showToast('Company logo uploaded successfully', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleCurrencyChange = async (newCurrency) => {
    if (!newCurrency || newCurrency === currency) return;
    const previous = currency;
    setCurrency(newCurrency);
    setSavingCurrency(true);
    setToast(null);
    try {
      await updateCompanyCurrency(newCurrency);
      if (typeof refreshUser === 'function') {
        await refreshUser();
      }
      showToast('Currency updated successfully', 'success');
    } catch (err) {
      setCurrency(previous);
      showToast(err.message, 'error');
    } finally {
      setSavingCurrency(false);
    }
  };

  const getInitials = () => {
    const first = firstName.charAt(0).toUpperCase();
    const last = lastName.charAt(0).toUpperCase();
    return first + last || first || '?';
  };

  if (showLoading) return <div className={settingsStyles.loading}>Loading...</div>;

  const accountSections = [
    { id: 'profile', label: 'Profile', Icon: ProfileIcon },
    { id: 'security', label: 'Security', Icon: SecurityIcon },
    { id: 'preferences', label: 'Preferences', Icon: PreferencesIcon },
  ];

  const workspaceSections = [
    ...(canManageCompanySettings ? [{ id: 'company', label: 'Company', Icon: CompanyIcon }] : []),
    ...(canManageBilling ? [{ id: 'billing', label: 'Billing', Icon: BillingIcon }] : []),
  ];

  const companyTabs = [
    { id: 'general', label: 'General', Icon: GeneralIcon },
    { id: 'labels', label: 'Labels', Icon: LabelsIcon },
    { id: 'roles', label: 'Team Roles', Icon: RolesIcon },
    { id: 'leave', label: 'Leave Types', Icon: LeaveIcon },
    { id: 'statuses', label: 'Project Statuses', Icon: StatusIcon },
    { id: 'actions', label: 'Lead Actions', Icon: ActionsIcon },
    { id: 'customfields', label: 'Custom Fields', Icon: CustomFieldsIcon },
  ];

  const activeCompanyTabLabel =
    companyTabs.find((t) => t.id === activeCompanyTab)?.label || 'General';

  const renderNavItem = ({ id, label, Icon }) => (
    <button
      key={id}
      className={`${settingsStyles.navItem} ${activeSection === id ? settingsStyles.active : ''}`}
      onClick={() => setActiveSection(id)}
    >
      <span className={settingsStyles.navIcon}>
        <Icon />
      </span>
      <span>{label}</span>
    </button>
  );

  return (
    <div className={settingsStyles.container}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className={settingsStyles.header}>
        <div className={settingsStyles.headerText}>
          <h1>Settings</h1>
          <p className={settingsStyles.headerSubtitle}>
            Manage your account and workspace preferences
          </p>
        </div>
      </div>

      <div className={settingsStyles.content}>
        <nav className={settingsStyles.sidebar}>
          <div className={settingsStyles.navCategory}>Account</div>
          {accountSections.map(renderNavItem)}

          {workspaceSections.length > 0 && (
            <>
              <div className={settingsStyles.navCategory}>Workspace</div>
              {workspaceSections.map(renderNavItem)}
            </>
          )}
        </nav>

        <div className={settingsStyles.main}>
          {activeSection === 'profile' && (
            <div className={settingsStyles.section}>
              <div className={settingsStyles.sectionHead}>
                <h2>Profile</h2>
                <p className={settingsStyles.sectionDesc}>
                  Manage your personal information and how others see you.
                </p>
              </div>

              <div className={`${settingsStyles.card} ${settingsStyles.heroCard}`}>
                <label htmlFor="avatar-input" className={settingsStyles.heroAvatar}>
                  {avatar ? (
                    <img src={avatar} alt="Profile" />
                  ) : (
                    <div className={settingsStyles.avatarPlaceholder}>
                      {getInitials()}
                    </div>
                  )}
                  <div className={settingsStyles.heroAvatarOverlay}>
                    <CameraIcon />
                    <span>Change</span>
                  </div>
                </label>
                <input
                  type="file"
                  id="avatar-input"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  style={{ display: 'none' }}
                />
                <div className={settingsStyles.heroInfo}>
                  <h3 className={settingsStyles.heroName}>
                    {`${firstName} ${lastName}`.trim() || 'Your name'}
                  </h3>
                  <p className={settingsStyles.heroEmail}>{email || 'No email set'}</p>
                  <div className={settingsStyles.heroMeta}>
                    <span className={settingsStyles.heroMetaItem}>
                      <span className={settingsStyles.heroMetaLabel}>Job title</span>
                      <span className={settingsStyles.heroMetaValue}>
                        {jobTitle || 'Not set'}
                      </span>
                    </span>
                    <span className={settingsStyles.heroMetaDivider} />
                    <span className={settingsStyles.heroMetaItem}>
                      <span className={settingsStyles.heroMetaLabel}>Company</span>
                      <span className={settingsStyles.heroMetaValue}>
                        {companyName || 'Not set'}
                      </span>
                    </span>
                  </div>
                  <p className={settingsStyles.hint}>
                    JPG, PNG or GIF. Max 2MB.
                  </p>
                </div>
              </div>

              <div className={settingsStyles.card}>
                <h3>Personal Information</h3>
                <p className={settingsStyles.cardDesc}>
                  Update the name, email, and contact number shown on your profile.
                </p>
                <form onSubmit={handleProfileSubmit}>
                  <div className={settingsStyles.formRow}>
                    <div className={styles.inputGroup}>
                      <label>First Name</label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="John"
                      />
                    </div>
                    <div className={styles.inputGroup}>
                      <label>Last Name</label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div className={styles.inputGroup}>
                    <label>Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  <div className={styles.inputGroup}>
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="+27 12 345 678"
                    />
                  </div>

                  <div className={settingsStyles.actions}>
                    <button
                      type="submit"
                      className={styles.saveButton}
                      disabled={!profileDirty}
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeSection === 'security' && (
            <div className={settingsStyles.section}>
              <div className={settingsStyles.sectionHead}>
                <h2>Security</h2>
                <p className={settingsStyles.sectionDesc}>
                  Manage your password and account security.
                </p>
              </div>

              <div className={settingsStyles.card}>
                <h3>Change Password</h3>
                <p className={settingsStyles.cardDesc}>
                  Use a strong password that you don't use elsewhere.
                </p>
                <form onSubmit={handlePasswordChange}>
                  <div className={styles.inputGroup}>
                    <label>Current Password</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                  </div>

                  <div className={styles.inputGroup}>
                    <label>New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <span className={settingsStyles.hint}>At least 8 characters.</span>
                  </div>

                  <div className={styles.inputGroup}>
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>

                  <div className={settingsStyles.actions}>
                    <button
                      type="submit"
                      className={styles.saveButton}
                      disabled={!currentPassword || !newPassword || !confirmPassword}
                    >
                      Update Password
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeSection === 'preferences' && (
            <div className={settingsStyles.section}>
              <div className={settingsStyles.sectionHead}>
                <h2>Preferences</h2>
                <p className={settingsStyles.sectionDesc}>
                  Customize your experience and notification settings.
                </p>
              </div>

              <div className={settingsStyles.card}>
                <h3>Notifications</h3>
                <p className={settingsStyles.cardDesc}>
                  Choose how you want to be notified for each event. In-App notifications appear in the notification bell. Email notifications are sent to your registered email address.
                </p>
                <div className={settingsStyles.notifMatrix}>
                  <div className={settingsStyles.notifMatrixHeader}>
                    <span className={settingsStyles.notifMatrixEventCol} />
                    <span className={settingsStyles.notifMatrixChannelLabel}>In-App</span>
                    <span className={settingsStyles.notifMatrixChannelLabel}>Email</span>
                  </div>
                  {NOTIFICATION_GROUPS.map((group) => (
                    <div key={group.label} className={settingsStyles.notifGroup}>
                      <div className={settingsStyles.notifGroupLabel}>{group.label}</div>
                      {group.events.map((evt) => {
                        const pref = notificationPrefs[evt.event_type] || { in_app: true, email: false };
                        return (
                          <div key={evt.event_type} className={settingsStyles.notifRow}>
                            <span className={settingsStyles.notifEventLabel}>{evt.label}</span>
                            <label className={settingsStyles.notifToggleCell}>
                              <input
                                type="checkbox"
                                checked={pref.in_app}
                                onChange={(e) => handlePrefToggle(evt.event_type, 'in_app', e.target.checked)}
                              />
                            </label>
                            <label className={settingsStyles.notifToggleCell}>
                              <input
                                type="checkbox"
                                checked={pref.email}
                                onChange={(e) => handlePrefToggle(evt.event_type, 'email', e.target.checked)}
                              />
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              <div className={`${settingsStyles.card} ${settingsStyles.comingSoonCard}`}>
                <div className={settingsStyles.comingSoonHeader}>
                  <h3>Display &amp; Formatting</h3>
                  <span className={settingsStyles.comingSoonBadge}>Coming soon</span>
                </div>
                <p className={settingsStyles.cardDesc}>
                  Personalization options will be available in a future update.
                </p>
                <div className={settingsStyles.formRow}>
                  <div className={styles.inputGroup}>
                    <label>Theme</label>
                    <CustomDropdown
                      fullWidth
                      value={theme}
                      onChange={(val) => setTheme(val)}
                      disabled
                      options={[
                        { value: 'light', label: 'Light' },
                        { value: 'dark', label: 'Dark' },
                        { value: 'system', label: 'System' },
                      ]}
                    />
                  </div>
                  <div className={styles.inputGroup}>
                    <label>Date Format</label>
                    <CustomDropdown
                      fullWidth
                      value={dateFormat}
                      onChange={(val) => setDateFormat(val)}
                      disabled
                      options={[
                        { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                        { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                        { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
                      ]}
                    />
                  </div>
                </div>
                <div className={styles.inputGroup}>
                  <label>Timezone</label>
                  <CustomDropdown
                    fullWidth
                    value={timezone}
                    onChange={(val) => setTimezone(val)}
                    disabled
                    options={[
                      { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (SAST)' },
                      { value: 'UTC', label: 'UTC' },
                      { value: 'America/New_York', label: 'America/New_York (EST)' },
                      { value: 'Europe/London', label: 'Europe/London (GMT)' },
                    ]}
                  />
                </div>
              </div>
            </div>
          )}

          {activeSection === 'company' && canManageCompanySettings && (
            <div className={settingsStyles.section}>
              <div className={settingsStyles.sectionHead}>
                <h2>Company</h2>
                <p className={settingsStyles.sectionDesc}>
                  <span className={settingsStyles.breadcrumb}>
                    Company <span className={settingsStyles.breadcrumbSep}>›</span>{' '}
                    <span className={settingsStyles.breadcrumbCurrent}>
                      {activeCompanyTabLabel}
                    </span>
                  </span>
                </p>
              </div>

              <div className={settingsStyles.subNav} role="tablist">
                {companyTabs.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    role="tab"
                    aria-selected={activeCompanyTab === id}
                    className={`${settingsStyles.subNavItem} ${
                      activeCompanyTab === id ? settingsStyles.subNavActive : ''
                    }`}
                    onClick={() => setActiveCompanyTab(id)}
                  >
                    <Icon />
                    <span>{label}</span>
                  </button>
                ))}
              </div>

              {activeCompanyTab === 'general' && (
                <>
                  <div className={settingsStyles.card}>
                    <h3>Company Branding</h3>
                    <p className={settingsStyles.cardDesc}>
                      This logo appears in the sidebar and on shared documents.
                    </p>
                    <div className={settingsStyles.brandingRow}>
                      <div className={settingsStyles.logoPreview}>
                        <img
                          src={companyLogo || '/favicon.png'}
                          alt="Company Logo"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/favicon.png';
                          }}
                        />
                      </div>
                      <div className={settingsStyles.brandingActions}>
                        <input
                          type="file"
                          id="logo-input"
                          accept="image/*"
                          onChange={handleCompanyLogoUpload}
                          style={{ display: 'none' }}
                        />
                        <label htmlFor="logo-input" className={styles.buttonPrimary}>
                          Upload Logo
                        </label>
                        <p className={settingsStyles.hint}>Recommended: 200×50px, PNG or SVG.</p>
                      </div>
                    </div>
                  </div>

                  <div className={settingsStyles.card}>
                    <h3>Company Information</h3>
                    <p className={settingsStyles.cardDesc}>
                      Contact support to update core company information.
                    </p>
                    <div className={settingsStyles.readOnlyGrid}>
                      <div className={settingsStyles.readOnlyItem}>
                        <span className={settingsStyles.readOnlyLabel}>Company Name</span>
                        <span className={settingsStyles.readOnlyValue}>
                          {companyName || 'Not set'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={settingsStyles.card}>
                    <h3>Currency</h3>
                    <p className={settingsStyles.cardDesc}>
                      Choose the currency used to display all monetary values
                      &mdash; budgets, costs, and rates &mdash; across the workspace.
                    </p>
                    <div className={styles.inputGroup}>
                      <label>Display Currency</label>
                      <CustomDropdown
                        fullWidth
                        value={currency}
                        onChange={handleCurrencyChange}
                        disabled={savingCurrency}
                        options={CURRENCIES.map((c) => ({
                          value: c.code,
                          label: `${c.code} — ${c.label} (${c.symbol})`,
                        }))}
                      />
                    </div>
                  </div>
                </>
              )}

              {activeCompanyTab === 'labels' && (
                <div className={settingsStyles.card}>
                  <LabelManager onMessage={setToast} />
                </div>
              )}

              {activeCompanyTab === 'roles' && (
                <div className={settingsStyles.card}>
                  <TeamDefaultsManager onMessage={setToast} />
                </div>
              )}

              {activeCompanyTab === 'leave' && (
                <div className={settingsStyles.card}>
                  <LeaveTypeManager onMessage={setToast} />
                </div>
              )}

              {activeCompanyTab === 'statuses' && (
                <div className={settingsStyles.card}>
                  <ProjectStatusManager onMessage={setToast} />
                </div>
              )}

              {activeCompanyTab === 'actions' && (
                <div className={settingsStyles.card}>
                  <LeadActionManager onMessage={setToast} />
                </div>
              )}

              {activeCompanyTab === 'customfields' && (
                <div className={settingsStyles.card}>
                  <CustomFieldManager onMessage={setToast} />
                </div>
              )}
            </div>
          )}

          {activeSection === 'billing' && canManageBilling && (
            <div className={settingsStyles.section}>
              <BillingManager />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
